import type { ListObrasQuery, MapObrasQuery, ObraListSummary, ObraStatus } from '@obras/shared';
import {
  formatCnpj,
  OBRA_PENDENCIA_LABELS,
  OBRA_PENDENCIAS,
  OBRA_STATUS_LABELS,
} from '@obras/shared';
import {
  Bell,
  CircleCheck,
  CircleDot,
  CircleSlash,
  HardHat,
  PauseCircle,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { Button } from '../../components/ui/Button.js';
import { SearchInput } from '../../components/ui/Field.js';
import {
  MultiSelect,
  SingleSelect,
  type MultiSelectOption,
} from '../../components/ui/MultiSelect.js';
import { cn } from '../../lib/cn.js';
import { useObraFilterOptions } from './api.js';

/*
 * O recorte do acervo de obras: filtros, busca e ordem.
 *
 * Partilhado pela listagem (cartões e lista) e pelo Mapa de Obras — as duas
 * telas filtram o mesmo acervo do mesmo jeito, e cada uma guarda o seu.
 */

const ICON = { strokeWidth: 1.5 } as const;

const STATUS_OPTIONS: readonly MultiSelectOption[] = (
  ['em_andamento', 'paralisada', 'concluida', 'cancelada'] as const satisfies readonly ObraStatus[]
).map((status) => ({ value: status, label: OBRA_STATUS_LABELS[status] }));

const KIND_OPTIONS: readonly MultiSelectOption[] = [
  { value: 'nova', label: 'Construção nova', detail: 'Obra erguida do zero' },
  { value: 'reforma', label: 'Reforma ou ampliação', detail: 'Intervenção em obra existente' },
];

/** O que pede ação na obra, com a mesma conta e os mesmos nomes do painel. */
const PENDENCIA_OPTIONS: readonly MultiSelectOption[] = OBRA_PENDENCIAS.map((pendencia) => ({
  value: pendencia,
  label: OBRA_PENDENCIA_LABELS[pendencia],
}));

/**
 * Como a lista é ordenada. Rótulo diz o critério, não o campo do banco.
 *
 * A data de cadastro vem primeiro porque é a ordem estável: "com atualizações"
 * muda de posição conforme quem abre e quando abriu, e a lista que se procura
 * duas vezes seguidas precisa estar no mesmo lugar nas duas.
 */
const SORT_OPTIONS: readonly MultiSelectOption[] = [
  { value: 'recent', label: 'Data de cadastro', detail: 'Mais recentes primeiro' },
  { value: 'updates', label: 'Com atualizações', detail: 'Novidades desde sua última visita' },
  { value: 'progress', label: 'Progresso', detail: 'Maior execução financeira primeiro' },
  { value: 'name', label: 'Ordem alfabética', detail: 'Pelo nome da obra' },
  { value: 'next_visit', label: 'Prazo de visita', detail: 'Vencendo primeiro' },
];

type SortOption = NonNullable<ListObrasQuery['sort']>;

/** O recorte como a tela o guarda: busca, filtros e ordem. */
export interface ObraFilterState {
  readonly search: string;
  readonly statuses: string[];
  readonly fiscalIds: string[];
  readonly companyIds: string[];
  readonly kinds: string[];
  readonly pendencias: string[];
  readonly sort: SortOption;
}

export const INITIAL_FILTERS: ObraFilterState = {
  search: '',
  statuses: [],
  fiscalIds: [],
  companyIds: [],
  kinds: [],
  pendencias: [],
  sort: 'recent',
};

/** O "limpar filtros": tira os filtros e mantém a busca e a ordem. */
export const NO_FILTERS = {
  statuses: [],
  fiscalIds: [],
  companyIds: [],
  kinds: [],
  pendencias: [],
} satisfies Partial<ObraFilterState>;

export function countActiveFilters(state: ObraFilterState): number {
  return (
    state.statuses.length +
    state.fiscalIds.length +
    state.companyIds.length +
    state.kinds.length +
    state.pendencias.length
  );
}

/**
 * Reconstrói o recorte guardado no navegador, campo a campo.
 *
 * Cada campo que não bate com o que a tela aceita hoje volta ao inicial sem
 * derrubar os demais: uma ordem que deixou de existir não deve apagar o filtro
 * de fiscal que estava ao lado.
 */
export function restoreFilters(raw: unknown): ObraFilterState | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const saved = raw as Record<string, unknown>;

  const strings = (value: unknown, allowed?: readonly MultiSelectOption[]): string[] =>
    Array.isArray(value)
      ? value.filter(
          (item): item is string =>
            typeof item === 'string' &&
            (allowed === undefined || allowed.some((option) => option.value === item)),
        )
      : [];

  return {
    search: typeof saved.search === 'string' ? saved.search : INITIAL_FILTERS.search,
    statuses: strings(saved.statuses, STATUS_OPTIONS),
    fiscalIds: strings(saved.fiscalIds),
    companyIds: strings(saved.companyIds),
    kinds: strings(saved.kinds, KIND_OPTIONS),
    pendencias: strings(saved.pendencias, PENDENCIA_OPTIONS),
    sort: SORT_OPTIONS.some((option) => option.value === saved.sort)
      ? (saved.sort as SortOption)
      : INITIAL_FILTERS.sort,
  };
}

/** Evita disparar uma requisição por tecla digitada. */
function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

/** O recorte no formato da API, com a busca esperando a digitação parar. */
export function useObraQueryFilters(state: ObraFilterState): Partial<MapObrasQuery> {
  const search = useDebounced(state.search);
  const { statuses, fiscalIds, companyIds, kinds, pendencias, sort } = state;

  return useMemo(
    () =>
      ({
        search: search || undefined,
        statuses: statuses as MapObrasQuery['statuses'],
        fiscalIds,
        companyIds,
        kinds: kinds as MapObrasQuery['kinds'],
        pendencias: pendencias as MapObrasQuery['pendencias'],
        sort,
      }) satisfies Partial<MapObrasQuery>,
    [search, statuses, fiscalIds, companyIds, kinds, pendencias, sort],
  );
}

interface RecorteProps {
  state: ObraFilterState;
  onChange: (patch: Partial<ObraFilterState>) => void;
}

/**
 * A barra de filtros: o que a tela tem de modo à esquerda, os filtros à
 * direita. Comando à direita, leitura à esquerda — a faixa de resumo, embaixo,
 * não disputa a mesma borda.
 */
export function ObraFilterBar({
  state,
  onChange,
  leading,
}: RecorteProps & {
  /** Antes do "Limpar filtros": o modo de visualização, na tela que tem. */
  leading?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <div className="mr-auto flex items-center gap-2">
        {leading}
        <LimparFiltros state={state} onChange={onChange} />
      </div>

      <FiltrosDoRecorte state={state} onChange={onChange} />
      <BuscaDoRecorte state={state} onChange={onChange} className="w-full sm:w-64" />
    </div>
  );
}

/** "Limpar filtros", só quando há filtro ligado: tira os filtros e mantém a busca e a ordem. */
export function LimparFiltros({ state, onChange }: RecorteProps) {
  if (countActiveFilters(state) === 0) return null;
  return (
    <Button variant="ghost" size="sm" onClick={() => onChange(NO_FILTERS)}>
      Limpar filtros
    </Button>
  );
}

/** A busca do recorte: nome da obra ou número do contrato. */
export function BuscaDoRecorte({
  state,
  onChange,
  className,
}: RecorteProps & { className?: string | undefined }) {
  return (
    <div className={cn('relative', className)}>
      <SearchInput
        value={state.search}
        onChange={(event) => onChange({ search: event.target.value })}
        onClear={() => onChange({ search: '' })}
        placeholder="Buscar obra ou contrato"
        aria-label="Buscar obras"
      />
    </div>
  );
}

/**
 * Os filtros do recorte — fiscal, tipo, situação, pendência e empresa — e a
 * ordem. Sem invólucro: quem usa decide como eles se arrumam na barra.
 */
export function FiltrosDoRecorte({
  state,
  onChange,
  classeDaOrdem = 'w-52',
}: RecorteProps & {
  /** A largura do seletor de ordem. */
  classeDaOrdem?: string | undefined;
}) {
  const options = useObraFilterOptions();

  const fiscalOptions: MultiSelectOption[] = (options.data?.fiscais ?? []).map((fiscal) => ({
    value: fiscal.id,
    label: fiscal.name,
  }));

  /*
   * A empresa é buscável por nome fantasia, razão social e CNPJ. O CNPJ entra
   * como termo oculto e formatado: quem procura tem o número no contrato, não
   * o nome exato que foi cadastrado.
   */
  const companyOptions: MultiSelectOption[] = (options.data?.companies ?? []).map((company) => ({
    value: company.id,
    label: company.tradeName ?? company.legalName,
    detail: [
      company.tradeName ? company.legalName : null,
      company.cnpj ? formatCnpj(company.cnpj) : null,
    ]
      .filter(Boolean)
      .join(' · '),
    terms: company.cnpj ? [company.cnpj, formatCnpj(company.cnpj)] : [],
  }));

  return (
    <>
      <MultiSelect
        label="Fiscal"
        options={fiscalOptions}
        selected={state.fiscalIds}
        onChange={(fiscalIds) => onChange({ fiscalIds })}
        searchable
      />
      <MultiSelect
        label="Tipo"
        options={KIND_OPTIONS}
        selected={state.kinds}
        onChange={(kinds) => onChange({ kinds })}
      />
      <MultiSelect
        label="Situação"
        options={STATUS_OPTIONS}
        selected={state.statuses}
        onChange={(statuses) => onChange({ statuses })}
      />
      <MultiSelect
        label="Pendência"
        options={PENDENCIA_OPTIONS}
        selected={state.pendencias}
        onChange={(pendencias) => onChange({ pendencias })}
      />
      <MultiSelect
        label="Empresa"
        options={companyOptions}
        selected={state.companyIds}
        onChange={(companyIds) => onChange({ companyIds })}
        searchable
      />

      <SingleSelect
        label="Ordenar por"
        options={SORT_OPTIONS}
        value={state.sort}
        onChange={(value) => onChange({ sort: value as SortOption })}
        className={classeDaOrdem}
      />
    </>
  );
}

/**
 * Situações que a faixa de resumo sempre mostra, mesmo zeradas.
 *
 * São as três que descrevem a rotina do órgão; sumir com a paralisada quando
 * ela chega a zero tiraria justamente a informação boa ("nenhuma parada").
 * Planejada e cancelada só aparecem quando existem, para não encompridar a
 * faixa com o que quase nunca é o caso.
 */
const ALWAYS_SHOWN: readonly ObraStatus[] = ['em_andamento', 'paralisada', 'concluida'];

const STAT_STYLE: Record<ObraStatus, { icon: LucideIcon; tone: string }> = {
  em_andamento: { icon: CircleDot, tone: 'text-ink-3' },
  paralisada: { icon: PauseCircle, tone: 'text-paused' },
  concluida: { icon: CircleCheck, tone: 'text-success' },
  cancelada: { icon: CircleSlash, tone: 'text-ink-3' },
};

const STAT_LABELS: Record<ObraStatus, [string, string]> = {
  em_andamento: ['em andamento', 'em andamento'],
  paralisada: ['paralisada', 'paralisadas'],
  concluida: ['concluída', 'concluídas'],
  cancelada: ['cancelada', 'canceladas'],
};

function Stat({
  icon: Icon,
  tone,
  value,
  label,
}: {
  icon: LucideIcon;
  tone: string;
  value: number | string;
  label: string;
}) {
  return (
    <span className="flex items-center gap-1.5 text-[13px] text-ink-3">
      <Icon {...ICON} size={14} className={cn('shrink-0', tone)} aria-hidden />
      <span className="tabular font-medium text-ink">{value}</span>
      {label}
    </span>
  );
}

/**
 * Faixa de resumo do recorte em tela.
 *
 * Fica embaixo e à esquerda, contrapeso à barra de filtros, que corre à
 * direita. É leitura, não comando: cada item diz quanto há de cada coisa
 * dentro do que os filtros deixaram passar.
 */
export function SummaryStrip({
  summary,
  total,
  showsUpdates,
}: {
  summary: ObraListSummary;
  total: number;
  showsUpdates: boolean;
}) {
  const all = Object.keys(STAT_STYLE) as ObraStatus[];
  const statuses = all.filter(
    (status) => ALWAYS_SHOWN.includes(status) || summary.byStatus[status] > 0,
  );

  /*
   * As parcelas por situação ignoram o filtro de situação, então elas somam o
   * acervo inteiro do recorte, não o que está paginado. Com filtro de situação
   * ligado, `total` seria menor que a soma das parcelas e a faixa se
   * contradiria; dizer "3 de 18" resolve sem esconder nem uma nem outra.
   */
  const universe = all.reduce((sum, status) => sum + summary.byStatus[status], 0);

  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
      <Stat
        icon={HardHat}
        tone="text-ink-3"
        value={total === universe ? total : `${total} de ${universe}`}
        label={universe === 1 ? 'obra' : 'obras'}
      />

      {statuses.map((status) => (
        <Stat
          key={status}
          icon={STAT_STYLE[status].icon}
          tone={STAT_STYLE[status].tone}
          value={summary.byStatus[status]}
          label={STAT_LABELS[status][summary.byStatus[status] === 1 ? 0 : 1]}
        />
      ))}

      {showsUpdates && summary.withUpdates > 0 ? (
        <Stat
          icon={Bell}
          tone="text-primary"
          value={summary.withUpdates}
          label={summary.withUpdates === 1 ? 'com novidade' : 'com novidades'}
        />
      ) : null}
    </div>
  );
}
